import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Input, Select, Space, Typography, message } from 'antd';
import { useCan } from '@refinedev/core';
import type { Dayjs } from 'dayjs';
import { SearchOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '@/services/api';
import { fromIstWallClock, toIstWallClock } from '@/utils/cricketCalendar';
import type { PvpEndReason, PvpMatchStatus } from '@/types/pvp';
import { MatchDrawer } from './MatchDrawer';
import { MatchesReport } from './MatchesReport';
import { PlayerDrawer } from './PlayerDrawer';
import {
  BOTS_OPTIONS,
  CLOSED_DRAWER,
  CLOSED_PLAYER,
  END_POLL_MS,
  INITIAL_MATCHES,
  REASON_OPTIONS,
  STATUS_OPTIONS,
  createDrawerLoader,
  createMatchesLoader,
  createPlayerLoader,
  endMatchRequest,
  endRequestPending,
  filtersError,
  filtersFromSearch,
  type DrawerState,
  type MatchBots,
  type MatchFilters,
  type MatchesState,
  type PlayerState,
} from './model';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * Realtime PvP (Rivals) match lookup: find a match by id, player id or name, or
 * browse the newest with filters. A row opens the match drawer (ball log loads
 * on open); a player name opens the player drawer. `?q=` fills the search box
 * and runs it. An admin who may edit players can end a stuck live match from
 * the drawer. Times are IST.
 */
export const PvpMatchesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<MatchFilters>(() =>
    filtersFromSearch(searchParams.toString())
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [matches, setMatches] = useState<MatchesState>(INITIAL_MATCHES);
  const [drawer, setDrawer] = useState<DrawerState>(CLOSED_DRAWER);
  const [player, setPlayer] = useState<PlayerState>(CLOSED_PLAYER);
  const { data: canEditPlayers } = useCan({ resource: 'players', action: 'edit' });

  const matchesLoader = useMemo(
    () => createMatchesLoader(adminApi.pvp.matches, (p) => setMatches((c) => ({ ...c, ...p }))),
    []
  );
  const drawerLoader = useMemo(
    () =>
      createDrawerLoader(adminApi.pvp.match, adminApi.pvp.balls, (p) =>
        setDrawer((c) => ({ ...c, ...p }))
      ),
    []
  );
  const playerLoader = useMemo(
    () => createPlayerLoader(adminApi.pvp.player, (p) => setPlayer((c) => ({ ...c, ...p }))),
    []
  );

  const search = (next: MatchFilters = filters) => {
    const now = new Date();
    const error = filtersError(next, now);
    setFormError(error);
    if (error) return;
    const q = next.q.trim();
    setSearchParams(q ? { q } : {}, { replace: true });
    void matchesLoader.search(next, now);
  };

  // First load only: the URL's q (or the newest matches) runs once; later
  // searches come from the button, Enter or the filters.
  useEffect(() => {
    void matchesLoader.search(filtersFromSearch(searchParams.toString()), new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchesLoader]);

  const setFilter = <K extends keyof MatchFilters>(key: K, value: MatchFilters[K]) =>
    setFilters((current) => ({ ...current, [key]: value }));

  // Changing a select searches at once; the text and the range wait for Search.
  const setAndSearch = <K extends keyof MatchFilters>(key: K, value: MatchFilters[K]) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    search(next);
  };

  // While an end request waits for a PvP server, re-read the open match every 10 s.
  const pendingId = drawer.match && endRequestPending(drawer.match) ? drawer.match.id : null;
  useEffect(() => {
    if (!pendingId) return undefined;
    const timer = setTimeout(() => {
      adminApi.pvp.match(pendingId).then(
        ({ data }) => setDrawer((c) => (c.id === pendingId ? { ...c, match: data } : c)),
        () => undefined // the next refresh, or reopening the match, tries again
      );
    }, END_POLL_MS);
    return () => clearTimeout(timer);
  }, [pendingId, drawer.match]);

  const endMatch = async (note: string): Promise<string | null> => {
    const id = drawer.id;
    if (!id) return 'No match is open';
    const answer = await endMatchRequest(adminApi.pvp.endMatch, id, note);
    if ('error' in answer) return answer.error;
    setDrawer((c) =>
      c.id === id && c.match ? { ...c, match: { ...c.match, endRequest: answer.request } } : c
    );
    message.success('End requested: a PvP server ends the match within about 10 s');
    return null;
  };

  const openMatch = (id: string) => {
    playerLoader.close();
    void drawerLoader.open(id);
  };
  const openPlayer = (userId: string) => {
    drawerLoader.close();
    void playerLoader.open(userId);
  };

  const rangeValue: [Dayjs, Dayjs] | null = filters.range
    ? [toIstWallClock(filters.range[0]), toIstWallClock(filters.range[1])]
    : null;

  return (
    <div>
      <Title level={3} style={{ marginBottom: 0 }}>
        PvP Matches
      </Title>
      <Text type="secondary">
        Find a Realtime PvP (Rivals) match by match id, player id or player name, or browse the
        newest. Click a match for the details and ball log, or a player name for their record. Times
        are IST.
      </Text>

      <Space wrap style={{ margin: '12px 0 4px' }}>
        <Input
          aria-label="Search"
          placeholder="Match id, player id or name"
          allowClear
          prefix={<SearchOutlined />}
          value={filters.q}
          onChange={(e) => setFilter('q', e.target.value)}
          onPressEnter={() => search()}
          style={{ width: 300 }}
        />
        <Select<PvpMatchStatus | 'any'>
          aria-label="Status"
          value={filters.status ?? 'any'}
          onChange={(v) => setAndSearch('status', v === 'any' ? undefined : v)}
          options={[{ value: 'any', label: 'Any status' }, ...STATUS_OPTIONS]}
          style={{ width: 130 }}
        />
        <Select<PvpEndReason | 'any'>
          aria-label="Reason"
          value={filters.reason ?? 'any'}
          onChange={(v) => setAndSearch('reason', v === 'any' ? undefined : v)}
          options={[{ value: 'any', label: 'Any reason' }, ...REASON_OPTIONS]}
          style={{ width: 170 }}
        />
        <Select<MatchBots>
          aria-label="Bots"
          value={filters.bots}
          onChange={(v) => setAndSearch('bots', v)}
          options={BOTS_OPTIONS}
          style={{ width: 140 }}
        />
        <RangePicker
          showTime={{ format: 'HH:mm' }}
          format="D MMM HH:mm"
          allowClear
          placeholder={['Last 7 days', 'now']}
          value={rangeValue}
          onChange={(dates) =>
            setFilter(
              'range',
              dates?.[0] && dates[1]
                ? [fromIstWallClock(dates[0]), fromIstWallClock(dates[1])]
                : null
            )
          }
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={() => search()}
          loading={matches.loading}
        >
          Search
        </Button>
      </Space>
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 12 }}>
        Default range: the last 7 days (at most 31). The range is read as IST and applies on Search.
        A name needs 2 or more letters.
      </Text>
      {formError && (
        <Alert type="warning" showIcon message={formError} style={{ marginBottom: 12 }} />
      )}

      <MatchesReport
        state={matches}
        onOpenMatch={openMatch}
        onOpenPlayer={openPlayer}
        onLoadMore={() => void matchesLoader.loadMore()}
      />

      <MatchDrawer
        open={drawer.id != null}
        onClose={() => drawerLoader.close()}
        state={drawer}
        onOpenPlayer={openPlayer}
        canEnd={canEditPlayers?.can === true}
        onEnd={endMatch}
      />
      <PlayerDrawer
        open={player.id != null}
        onClose={() => playerLoader.close()}
        state={player}
        onOpenMatch={openMatch}
        onOpenPlayer={openPlayer}
      />
    </div>
  );
};

export default PvpMatchesPage;
